import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';
import type {
    MyMgeSubmissionForEventQuery,
    MyMgeSubmissionForEventQueryVariables,
} from 'generated/graphql';
import { MyMgeSubmissionForEventDocument } from 'generated/documents/MyMgeSubmissionForEventDocument';

export const HDE_EXISTING_SUBMISSION_API_ROUTE =
    '/remix-api/app/hde-existing-submission';

const QUERY_SCHEMA = z.object({
    eventId: z.coerce.number().int().positive(),
});

export type HDEApplicationType = 'Homeowner' | 'Professional' | 'Exhibitor';

export interface HDEExistingSubmission {
    id: string;
    applicationType: HDEApplicationType | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    /** Submitted values keyed by the raw DataObject field name, for prefilling. */
    fields: Record<string, string>;
}

interface ExistingSubmissionResponse {
    eventId?: number;
    submission: HDEExistingSubmission | null;
    /**
     * True when the member is an invited guest on this event (live ticket, no primary
     * submission). The modal blocks primary registration and explains why.
     */
    isEventGuest: boolean;
    error?: string;
}

const toApplicationType = (
    value: string | null | undefined,
): HDEApplicationType | null => {
    if (
        value === 'Homeowner' ||
        value === 'Professional' ||
        value === 'Exhibitor'
    ) {
        return value;
    }
    return null;
};

/**
 * API endpoint returning the current member's existing HDE submission for an event,
 * or null when they have not registered (or are not authenticated). Drives the
 * "you've already registered — update?" prompt and form prefill in the modal.
 *
 * The underlying GraphQL query derives the account email server-side and throws for
 * guests; we treat any error uniformly as "no existing submission" so the caller has
 * a single, simple shape to branch on.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        eventId: url.searchParams.get('eventId'),
    });

    if (!result.success) {
        return routerData<ExistingSubmissionResponse>(
            {
                submission: null,
                isEventGuest: false,
                error: 'Invalid query parameters',
            },
            { status: 400 },
        );
    }

    try {
        const queryResult = await context.client.query<
            MyMgeSubmissionForEventQuery,
            MyMgeSubmissionForEventQueryVariables
        >({
            query: MyMgeSubmissionForEventDocument,
            variables: { EventID: result.data.eventId },
        });

        const isEventGuest = queryResult.data?.myMGEIsEventGuest ?? false;
        const node = queryResult.data?.myMGESubmissionForEvent;
        if (!node) {
            return routerData<ExistingSubmissionResponse>({
                eventId: result.data.eventId,
                submission: null,
                isEventGuest,
            });
        }

        const fields: Record<string, string> = {};
        for (const field of node.FormFields) {
            if (field.FieldName && field.Value != null) {
                fields[field.FieldName] = field.Value;
            }
        }

        return routerData<ExistingSubmissionResponse>({
            eventId: result.data.eventId,
            isEventGuest,
            submission: {
                id: node.ID,
                applicationType: toApplicationType(node.ApplicationType),
                firstName: node.FirstName ?? null,
                lastName: node.LastName ?? null,
                email: node.Email ?? null,
                phone: node.Phone ?? null,
                fields,
            },
        });
    } catch (error) {
        // Guests (NotAuthorizedException) and transient errors collapse to "no
        // existing submission" — the modal simply proceeds with a fresh registration.
        // Surface the underlying GraphQL/network detail (the bare error serialises to
        // just "Invariant Violation", which hides the real cause).
        const err = error as {
            name?: string;
            message?: string;
            graphQLErrors?: ReadonlyArray<{
                message?: string;
                path?: unknown;
                extensions?: unknown;
            }>;
            networkError?: {
                message?: string;
                statusCode?: number;
                result?: unknown;
            } | null;
            clientErrors?: ReadonlyArray<{ message?: string }>;
        };
        context.logger.error(`Error in ${HDE_EXISTING_SUBMISSION_API_ROUTE}`, {
            name: err?.name,
            message: err?.message,
            graphQLErrors: err?.graphQLErrors?.map((graphQLError) => ({
                message: graphQLError?.message,
                path: graphQLError?.path,
                extensions: graphQLError?.extensions,
            })),
            networkError: err?.networkError
                ? {
                      message: err.networkError.message,
                      statusCode: err.networkError.statusCode,
                      result: err.networkError.result,
                  }
                : null,
            clientErrors: err?.clientErrors?.map(
                (clientError) => clientError?.message,
            ),
        });
        return routerData<ExistingSubmissionResponse>({
            eventId: result.data.eventId,
            submission: null,
            isEventGuest: false,
        });
    }
};
